export type HubCalendarEvent = {
	id?: number;
	title: string;
	day: string;
	start?: string;
	end?: string;
	note?: string;
	allDay?: boolean;
};

export type HubCalendarTask = {
	id: string;
	title: string;
	dueOn: string;
	tags: string;
};

export type HubCalendarWork = {
	id?: number;
	title: string;
	due: string;
	start?: string;
};

export type HubCalendarItem =
	| { kind: "event"; key: string; title: string; start: string; eventId?: number }
	| { kind: "task"; key: string; title: string; start: string; taskId: string }
	| { kind: "work"; key: string; title: string; start: string; workId: number };

export function isoDay(date: Date): string {
	const y = date.getFullYear();
	const m = String(date.getMonth() + 1).padStart(2, "0");
	const d = String(date.getDate()).padStart(2, "0");
	return `${y}-${m}-${d}`;
}

export function monthCells(cursor: Date): string[] {
	const year = cursor.getFullYear();
	const month = cursor.getMonth();
	const today = isoDay(new Date());
	const first = new Date(year, month, 1);
	const lead = (first.getDay() + 6) % 7;
	const count = new Date(year, month + 1, 0).getDate();
	const cells = Array.from({ length: lead + count }, (_, index) => {
		if (index < lead) {
			const date = new Date(year, month, index - lead + 1);
			return isoDay(date);
		}
		return isoDay(new Date(year, month, index - lead + 1));
	});
	while (cells.length % 7) {
		const last = cells[cells.length - 1] ?? today;
		const date = new Date(`${last}T12:00:00`);
		date.setDate(date.getDate() + 1);
		cells.push(isoDay(date));
	}
	return cells;
}

function isInstallTag(name: string): boolean {
	return name.trim().toLowerCase() === "install";
}

function taskTagNames(tags: string): string[] {
	return tags.split(",").map((part) => part.trim()).filter(Boolean);
}

export function workDueDay(due: string): string {
	const trimmed = due.trim();
	if (!trimmed) return "";
	if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
	const parsed = new Date(trimmed.includes("T") ? trimmed : `${trimmed}T12:00:00`);
	if (Number.isNaN(parsed.getTime())) return trimmed.slice(0, 10);
	return isoDay(parsed);
}

export function workDueStart(due: string, start?: string): string {
	const explicit = start?.trim() ?? "";
	if (/^\d{1,2}:\d{2}/.test(explicit)) {
		const [hour, minute] = explicit.split(":");
		return `${hour!.padStart(2, "0")}:${minute!.slice(0, 2)}`;
	}
	const fromDue = due.trim().match(/T(\d{2}:\d{2})/);
	return fromDue?.[1] ?? "";
}

export function calendarTasksForDisplay(tasks: HubCalendarTask[]): HubCalendarTask[] {
	const seen = new Set<string>();
	const rows: HubCalendarTask[] = [];
	for (const task of tasks) {
		const id = task.id.trim();
		if (!id || seen.has(id)) continue;
		seen.add(id);
		if (taskTagNames(task.tags).some(isInstallTag)) continue;
		if (!task.dueOn.trim()) continue;
		rows.push(task);
	}
	return rows;
}

export function calendarWorkForDisplay(work: HubCalendarWork[]): HubCalendarWork[] {
	const seen = new Set<number>();
	const rows: HubCalendarWork[] = [];
	for (const row of work) {
		if (!workDueDay(row.due)) continue;
		const id = row.id;
		if (typeof id === "number") {
			if (seen.has(id)) continue;
			seen.add(id);
		}
		rows.push(row);
	}
	return rows;
}

function compareCalendarStart(left: string, right: string): number {
	if (left === right) return 0;
	if (!left) return 1;
	if (!right) return -1;
	return left.localeCompare(right);
}

function sortItemsByStart(rows: HubCalendarItem[]): HubCalendarItem[] {
	return [...rows].sort((left, right) => {
		const byStart = compareCalendarStart(left.start, right.start);
		if (byStart !== 0) return byStart;
		return left.title.localeCompare(right.title, undefined, { sensitivity: "base" });
	});
}

function sortCalendarItems(rows: HubCalendarItem[]): HubCalendarItem[] {
	const events = sortItemsByStart(rows.filter((row) => row.kind === "event"));
	const tasks = sortItemsByStart(rows.filter((row) => row.kind === "task"));
	const work = sortItemsByStart(rows.filter((row) => row.kind === "work"));
	return [...events, ...tasks, ...work];
}

export function hubCalendarItemsForDay(
	day: string,
	options: {
		events: HubCalendarEvent[];
		tasks: HubCalendarTask[];
		work: HubCalendarWork[];
		showEvents: boolean;
		showTasks: boolean;
		showWork: boolean;
	},
): HubCalendarItem[] {
	const rows: HubCalendarItem[] = [];
	if (options.showEvents) {
		for (const event of options.events) {
			if (event.day !== day) continue;
			const eventId = typeof event.id === "number" ? event.id : undefined;
			rows.push({
				kind: "event",
				key: eventId != null ? `event-${eventId}` : `event-${day}-${event.title}-${event.start ?? ""}`,
				title: event.title,
				start: event.allDay ? "" : event.start ?? "",
				eventId,
			});
		}
	}
	if (options.showTasks) {
		for (const task of calendarTasksForDisplay(options.tasks)) {
			if (task.dueOn !== day) continue;
			rows.push({
				kind: "task",
				key: `task-${task.id}`,
				title: task.title,
				start: "",
				taskId: task.id,
			});
		}
	}
	if (options.showWork) {
		for (const job of calendarWorkForDisplay(options.work)) {
			if (workDueDay(job.due) !== day) continue;
			const workId = job.id;
			if (typeof workId !== "number") continue;
			rows.push({
				kind: "work",
				key: `work-${workId}`,
				title: job.title,
				start: workDueStart(job.due, job.start),
				workId,
			});
		}
	}
	return sortCalendarItems(rows);
}
